import React, { useCallback, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Typography
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { PortalLoadingCard, PortalStateCard, derivePortalTheme } from './portalShared'

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 'var(--df-radius-md)',
    boxShadow: 'var(--df-shadow-sm)',
    border: '1px solid var(--df-line)'
  },
  cardTitle: {
    fontWeight: 700,
    color: 'var(--df-ink)'
  },
  helperText: {
    color: 'var(--df-ink-3)'
  },
  colorRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5)
  },
  colorSwatch: {
    width: 44,
    height: 44,
    borderRadius: 'var(--df-radius-md)',
    border: '2px solid var(--df-line)',
    cursor: 'pointer',
    padding: 0,
    appearance: 'none',
    '&::-webkit-color-swatch-wrapper': { padding: 0 },
    '&::-webkit-color-swatch': { border: 'none', borderRadius: 'var(--df-radius-sm)' }
  },
  previewStrip: {
    borderRadius: 'var(--df-radius-md)',
    overflow: 'hidden',
    border: '1px solid var(--df-line)',
    marginTop: theme.spacing(2)
  },
  logoZone: {
    border: '2px dashed var(--df-line-2)',
    borderRadius: 'var(--df-radius-md)',
    padding: theme.spacing(3),
    textAlign: 'center',
    cursor: 'pointer',
    '&:hover': { borderColor: 'var(--df-ink-4)' }
  },
  logoPreview: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  actionRow: {
    display: 'flex',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(3)
  }
}))

const SubcontractorSettings = ({ user, notification }) => {
  const classes = useStyles()
  const fileInputRef = useRef(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [primaryColor, setPrimaryColor] = useState('')
  const [logoUrl, setLogoUrl] = useState('')
  // Applicator info — only visible to herbicide-flagged subs
  const [subcontractorTypes, setSubcontractorTypes] = useState([])
  const [applicatorLicense, setApplicatorLicense] = useState('')
  const [pmdNumber, setPmdNumber] = useState('')
  const [qualifyingParty, setQualifyingParty] = useState('')
  const [applicatorPhone, setApplicatorPhone] = useState('')
  const [applicatorAddress, setApplicatorAddress] = useState('')

  const showApplicatorInfo = Array.isArray(subcontractorTypes) && subcontractorTypes.includes('herbicide')

  const loadBranding = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await axios.get('/api/subcontractor/branding')
      setDisplayName(res.data?.displayName || '')
      setPrimaryColor(res.data?.primaryColor || '')
      setLogoUrl(res.data?.logoUrl || '')
      setSubcontractorTypes(res.data?.subcontractorTypes || [])
      setApplicatorLicense(res.data?.applicatorLicense || '')
      setPmdNumber(res.data?.pmdNumber || '')
      setQualifyingParty(res.data?.qualifyingParty || '')
      setApplicatorPhone(res.data?.applicatorPhone || '')
      setApplicatorAddress(res.data?.applicatorAddress || '')
    } catch (err) {
      console.error('Failed to load branding', err)
      setError(err?.response?.data?.message || 'Unable to load branding settings')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { loadBranding() }, [loadBranding])

  const handleSave = async () => {
    setSaving(true)
    try {
      const body = { displayName, primaryColor }
      if (showApplicatorInfo) {
        body.applicatorLicense = applicatorLicense
        body.pmdNumber = pmdNumber
        body.qualifyingParty = qualifyingParty
        body.applicatorPhone = applicatorPhone
        body.applicatorAddress = applicatorAddress
      }
      const res = await axios.put('/api/subcontractor/branding', body)
      setDisplayName(res.data?.displayName || '')
      setPrimaryColor(res.data?.primaryColor || '')
      setApplicatorLicense(res.data?.applicatorLicense || '')
      setPmdNumber(res.data?.pmdNumber || '')
      setQualifyingParty(res.data?.qualifyingParty || '')
      setApplicatorPhone(res.data?.applicatorPhone || '')
      setApplicatorAddress(res.data?.applicatorAddress || '')
      notification?.({ type: 'success', title: 'Settings Saved', message: 'Portal settings updated.' })
    } catch (err) {
      console.error('Failed to save branding', err)
      notification?.({ type: 'danger', title: 'Save Failed', message: err?.response?.data?.message || 'Unable to save settings' })
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    setSaving(true)
    try {
      const res = await axios.put('/api/subcontractor/branding', { displayName: '', primaryColor: '' })
      setDisplayName(res.data?.displayName || '')
      setPrimaryColor(res.data?.primaryColor || '')
      notification?.({ type: 'success', title: 'Branding Reset', message: 'Portal branding reset to defaults.' })
    } catch (err) {
      console.error('Failed to reset branding', err)
      notification?.({ type: 'danger', title: 'Reset Failed', message: err?.response?.data?.message || 'Unable to reset branding' })
    } finally {
      setSaving(false)
    }
  }

  const handleLogoUpload = async (file) => {
    if (!file) return
    const formData = new FormData()
    formData.append('logo', file)
    setUploading(true)
    try {
      const res = await axios.post('/api/subcontractor/branding/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      setLogoUrl(res.data?.logoUrl || '')
      notification?.({ type: 'success', title: 'Logo Uploaded', message: 'Your logo will appear in the portal header.' })
    } catch (err) {
      console.error('Failed to upload logo', err)
      notification?.({ type: 'danger', title: 'Upload Failed', message: err?.response?.data?.message || 'Unable to upload logo' })
    } finally {
      setUploading(false)
    }
  }

  const handleLogoRemove = async () => {
    setUploading(true)
    try {
      await axios.delete('/api/subcontractor/branding/logo')
      setLogoUrl('')
      notification?.({ type: 'success', title: 'Logo Removed', message: 'Logo has been removed from the portal.' })
    } catch (err) {
      console.error('Failed to remove logo', err)
      notification?.({ type: 'danger', title: 'Remove Failed', message: err?.response?.data?.message || 'Unable to remove logo' })
    } finally {
      setUploading(false)
    }
  }

  const previewTheme = derivePortalTheme(primaryColor)
  const previewName = displayName || user?.subcontractorProfile?.vendorName || 'Your Company'

  if (loading) {
    return <PortalLoadingCard title="Loading settings" message="Pulling portal branding configuration." minHeight={240} />
  }

  if (error) {
    return <PortalStateCard title="Settings unavailable" message={error} actionLabel="Retry" onAction={loadBranding} tone="danger" minHeight={240} />
  }

  return (
    <>
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="h6" className={classes.cardTitle}>Portal Branding</Typography>
        <Typography variant="body2" className={classes.helperText}>Customize how your portal looks for your team.</Typography>

        <Box mt={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                helperText="Shown in the header. Leave blank to use your vendor name."
                inputProps={{ maxLength: 100 }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--df-ink-3)', marginBottom: 6 }}>Primary Color</Typography>
              <div className={classes.colorRow}>
                <input
                  type="color"
                  className={classes.colorSwatch}
                  value={primaryColor || '#1f5e3a'}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                />
                <TextField
                  variant="outlined"
                  size="small"
                  value={primaryColor}
                  onChange={(e) => setPrimaryColor(e.target.value)}
                  placeholder="#1f5e3a"
                  style={{ flex: 1 }}
                />
              </div>
              <Typography variant="caption" style={{ color: 'var(--df-ink-3)', marginTop: 4, display: 'block' }}>
                Controls header, tabs, and accent colors throughout the portal.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--df-ink-3)', marginBottom: 8 }}>Preview</Typography>
              <div className={classes.previewStrip}>
                <div style={{ background: previewTheme.primary, padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 8, height: 40 }}>
                  {logoUrl ? <img src={logoUrl} alt="" style={{ height: 22, objectFit: 'contain', borderRadius: 3 }} /> : null}
                  <span style={{ color: previewTheme.textOnPrimary, fontWeight: 700, fontSize: 13 }}>{previewName}</span>
                </div>
                <div style={{ background: 'transparent', padding: '4px 16px', display: 'flex', gap: 0, borderBottom: '1px solid var(--df-line)' }}>
                  <span style={{ color: 'var(--df-ink)', padding: '4px 10px', fontSize: 11, fontWeight: 600, borderBottom: `2px solid ${previewTheme.primary}` }}>Dashboard</span>
                  <span style={{ color: 'var(--df-ink-3)', padding: '4px 10px', fontSize: 11, fontWeight: 500 }}>Schedule</span>
                  <span style={{ color: 'var(--df-ink-3)', padding: '4px 10px', fontSize: 11, fontWeight: 500 }}>Settings</span>
                </div>
              </div>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="body2" style={{ fontWeight: 600, color: 'var(--df-ink-3)', marginBottom: 8 }}>Logo</Typography>
              {logoUrl ? (
                <div className={classes.logoPreview}>
                  <img src={logoUrl} alt="Current logo" style={{ height: 48, objectFit: 'contain', borderRadius: 6, border: '1px solid var(--df-line)' }} />
                  <Button className="df-danger" size="small" onClick={handleLogoRemove} disabled={uploading}>
                    {uploading ? 'Removing...' : 'Remove Logo'}
                  </Button>
                </div>
              ) : (
                <div
                  className={classes.logoZone}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleLogoUpload(e.dataTransfer.files[0]) }}
                >
                  <Typography variant="body2" style={{ color: 'var(--df-ink-3)' }}>
                    {uploading ? 'Uploading...' : 'Drop an image here or click to upload'}
                  </Typography>
                  <Typography variant="caption" style={{ color: 'var(--df-ink-4)' }}>PNG or JPG, max 2MB</Typography>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                style={{ display: 'none' }}
                onChange={(e) => handleLogoUpload(e.target.files[0])}
              />
            </Grid>
          </Grid>
        </Box>

        <div className={classes.actionRow}>
          <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
          <Button variant="outlined" onClick={handleReset} disabled={saving}>
            Reset Branding to Default
          </Button>
        </div>
      </CardContent>
    </Card>

    {showApplicatorInfo && (
      <Card className={classes.card} style={{ marginTop: 16 }}>
        <CardContent>
          <Typography variant="h6" className={classes.cardTitle}>Applicator Information</Typography>
          <Typography variant="body2" className={classes.helperText}>
            Used on spray-log PDFs. The applicator who actually performs the spray is captured in the field app per job;
            these fields are your company's regulatory identity (qualifying party, licenses, etc.).
          </Typography>
          <Box mt={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Qualifying Party"
                  variant="outlined"
                  value={qualifyingParty}
                  onChange={(e) => setQualifyingParty(e.target.value)}
                  inputProps={{ maxLength: 120 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PMD Number"
                  variant="outlined"
                  value={pmdNumber}
                  onChange={(e) => setPmdNumber(e.target.value)}
                  inputProps={{ maxLength: 60 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Applicator License #"
                  variant="outlined"
                  value={applicatorLicense}
                  onChange={(e) => setApplicatorLicense(e.target.value)}
                  inputProps={{ maxLength: 60 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  variant="outlined"
                  value={applicatorPhone}
                  onChange={(e) => setApplicatorPhone(e.target.value)}
                  inputProps={{ maxLength: 40 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  variant="outlined"
                  value={applicatorAddress}
                  onChange={(e) => setApplicatorAddress(e.target.value)}
                  helperText="Your business address — appears on the spray log when you're the document owner."
                  inputProps={{ maxLength: 240 }}
                />
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    )}
    </>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  notification: state.notification
})

export default connect(mapStateToProps)(SubcontractorSettings)
